// What a connection received from its transmitter, per product, as the receptora reads it: each
// record's stored columns and the transmitter's record as the receiver stored it, every identity
// document masked before it left the receiver.
import { Schema } from 'effect';
import { IsoDateTimeUtc } from '@finnest/core/effect';
import { pageOf } from '../receiver.ts';

const Text = Schema.NonEmptyString;
const Instant = IsoDateTimeUtc;

/** The products a connection's received data is read by. */
export const OPIN_RECEIVER_PRODUCTS = ['policies', 'premiums', 'claims', 'policyholders'] as const;
export const OpinReceiverProduct = Schema.Literals(OPIN_RECEIVER_PRODUCTS);
export type OpinReceiverProduct = typeof OpinReceiverProduct.Type;

const Money = Schema.NullOr(Schema.String);
const Day = Schema.NullOr(Schema.String);

/**
 * The transmitter's record as the receiver stored it, every identity document masked before it
 * left the receiver. A claim synced before the receiver kept claim records has none.
 */
const StoredRecord = Schema.Json;

export const OpinReceiverPolicy = Schema.Struct({
  id: Text,
  type: Text,
  number: Text,
  status: Schema.NullOr(Schema.String),
  startDate: Day,
  endDate: Day,
  premiumAmount: Money,
  syncedAt: Instant,
  rawData: StoredRecord,
});
export const OpinReceiverPremium = Schema.Struct({
  id: Text,
  policyId: Text,
  policyNumber: Text,
  amount: Money,
  dueDate: Day,
  status: Schema.NullOr(Schema.String),
  paidAt: Schema.NullOr(Instant),
  syncedAt: Instant,
  rawData: StoredRecord,
});
export const OpinReceiverClaim = Schema.Struct({
  id: Text,
  policyId: Text,
  policyNumber: Text,
  status: Text,
  claimedAmount: Money,
  settledAmount: Money,
  filedAt: Schema.NullOr(Instant),
  occurredAt: Schema.NullOr(Instant),
  rawData: Schema.NullOr(StoredRecord),
});
export const OpinReceiverPolicyholder = Schema.Struct({
  id: Text,
  name: Schema.String,
  email: Schema.NullOr(Schema.String),
  phone: Schema.NullOr(Schema.String),
  syncedAt: Instant,
  /** Only the record's names: the receiver stores no other member of it (#1841). */
  rawData: StoredRecord,
});

export type OpinReceiverPolicy = typeof OpinReceiverPolicy.Type;
export type OpinReceiverPremium = typeof OpinReceiverPremium.Type;
export type OpinReceiverClaim = typeof OpinReceiverClaim.Type;
export type OpinReceiverPolicyholder = typeof OpinReceiverPolicyholder.Type;

export const OpinReceiverPolicyPage = pageOf(OpinReceiverPolicy);
export const OpinReceiverPremiumPage = pageOf(OpinReceiverPremium);
export const OpinReceiverClaimPage = pageOf(OpinReceiverClaim);
export const OpinReceiverPolicyholderPage = pageOf(OpinReceiverPolicyholder);
export type OpinReceiverPolicyPage = typeof OpinReceiverPolicyPage.Type;
export type OpinReceiverPremiumPage = typeof OpinReceiverPremiumPage.Type;
export type OpinReceiverClaimPage = typeof OpinReceiverClaimPage.Type;
export type OpinReceiverPolicyholderPage = typeof OpinReceiverPolicyholderPage.Type;

/** The page window of a received-data list. */
export const OpinReceiverPageQuery = Schema.Struct({
  page: Schema.optionalKey(
    Schema.NumberFromString.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  ),
});
